import React, { ReactElement, useEffect, useState } from 'react'
import {
  NativeSyntheticEvent,
  Platform,
  Pressable,
  PressableProps,
  StyleProp,
  StyleSheet,
  TargetedEvent,
  View,
  ViewProps,
  ViewStyle,
} from 'react-native'
import { Href, Link } from 'zens-router'

import { Icon } from '../../component/icon'
import { Text } from '../../component/typography'
import { Color, Spacing } from '../../style'

import { TableCol } from './table'

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: Color.simplyWhite,
    paddingHorizontal: Spacing.S,
    minHeight: 64,
    alignItems: 'center',
  },
  condensed: {
    minHeight: 50,
  },
  cell: {
    flex: 1,
    marginHorizontal: Spacing.XXS,
    justifyContent: 'center',
  },
  hovered: {
    backgroundColor: Color.sleepingGrey,
  },
  active: {
    backgroundColor: Color.selectedBlue,
  },
  pressed: {
    backgroundColor: Color.lightGreyBlue,
  },
  disabled: {
    backgroundColor: Color.sleepingGrey,
  },
  disabledCell: {
    opacity: 0.4,
  },
  disclosure: {
    color: Color.metalGrey,
  },
  accessory: {
    width: 10,
  },
  remove: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
})

export interface TablRowSize {
  flex?: number
  width?: number
}

export interface Props extends Omit<PressableProps, 'style'> {
  cols: (string | number | ReactElement)[]
  sizes?: (TablRowSize | undefined)[]
  accessory?: false | ReactElement
  hasAccessory?: boolean
  style?: StyleProp<ViewStyle>
  cellStyle?: StyleProp<ViewStyle>
  condensed?: boolean
  active?: boolean
  onRemove?: () => void
  href?: Href
}
const Row = (props: Props) => {
  const {
    cols,
    sizes,
    accessory,
    hasAccessory,
    style,
    cellStyle,
    condensed,
    disabled,
    onRemove,
    onFocus,
    onBlur,
    onPress,
    href,
    ...otherProps
  } = props
  const [active, setActive] = useState(false)
  const [removeActive, setRemoveActive] = useState(false)

  const updateActive = (value: boolean) => (event: NativeSyntheticEvent<TargetedEvent>) => {
    setActive(value)
    value && onFocus && onFocus(event)
    !value && onBlur && onBlur(event)
  }

  useEffect(() => {
    if (disabled) {
      setActive(false)
      setRemoveActive(false)
    }
  }, [disabled])

  useEffect(() => {
    setActive(!!props.active)
  }, [props.active])

  function renderRemove(state: { pressed: boolean; hovered?: boolean }) {
    const { hovered } = state
    return hovered && <Icon name="trash" size={18} color={removeActive ? Color.soothingPink : Color.premiumBlack} />
  }

  const renderRow = (state: { pressed: boolean; hovered?: boolean }) => {
    const { pressed, hovered } = state

    const containerStyle = [
      styles.container,
      condensed && styles.condensed,
      hovered && styles.hovered,
      active && styles.active,
      pressed && styles.pressed,
      disabled && styles.disabled,
      style,
    ]

    const cellContentStyle = [styles.cell, disabled && styles.disabledCell, cellStyle]
    return (
      <View style={containerStyle}>
        {cols.map((col, index) => {
          const size = sizes?.[index]
          const sizeStyle: (ViewStyle | false)[] = [
            size?.width !== undefined && { maxWidth: size?.width },
            size?.flex !== undefined && { flex: size?.flex },
          ]
          return (
            <View style={[cellContentStyle, sizeStyle]} key={`col-${index}`}>
              {typeof col === 'string' || typeof col === 'number' ? <Text>{col}</Text> : col}
            </View>
          )
        })}
        {onRemove && (
          <Pressable
            onPress={onRemove}
            onFocus={() => setRemoveActive(true)}
            onBlur={() => setRemoveActive(false)}
            style={[
              Platform.select({
                // @ts-expect-error outline style none is not defined
                web: {
                  outlineStyle: 'none',
                } as ViewStyle,
              }),
              styles.remove,
            ]}
            testID="TableRow.Remove"
          >
            {renderRemove}
          </Pressable>
        )}
        {hasAccessory && (
          <View style={styles.accessory}>
            {onPress && accessory !== false && (accessory || <Icon name="right" size={14} style={styles.disclosure} />)}
          </View>
        )}
      </View>
    )
  }
  if (onPress || href) {
    const pressable = (
      <Pressable
        onPress={onPress}
        onFocus={updateActive(true)}
        onBlur={updateActive(false)}
        style={Platform.select({
          // @ts-expect-error outline style none is not defined
          web: {
            outlineStyle: 'none',
          } as ViewStyle,
        })}
        {...otherProps}
      >
        {renderRow}
      </Pressable>
    )
    if (href) {
      return (
        <Link href={href} asChild>
          {pressable}
        </Link>
      )
    } else {
      return pressable
    }
  } else {
    return renderRow({ pressed: false, hovered: false })
  }
}

interface SkeletonProps extends ViewProps {
  cols?: TableCol<any>[]
  hasAccessory?: boolean
  condensed?: boolean
  contentStyle?: StyleProp<ViewStyle>
}

Row.Skeleton = (props: SkeletonProps) => {
  const { cols, hasAccessory, condensed, contentStyle, style, ...otherProps } = props

  const sizes = cols?.map((col) => col.size)

  const containerStyle = [styles.container, condensed && styles.condensed, style]

  return (
    <View style={containerStyle} {...otherProps}>
      {cols?.map((col, index) => {
        const size = sizes?.[index]
        const sizeStyle: (ViewStyle | false)[] = [
          size?.width !== undefined && { maxWidth: size?.width },
          size?.flex !== undefined && { flex: size?.flex },
        ]
        const style = [index !== cols.length - 1 && { marginRight: Spacing.S }, contentStyle]
        return (
          <View style={[styles.cell, sizeStyle]} key={`col-${index}`}>
            <Text.Skeleton style={style} />
            <View style={hasAccessory && styles.accessory} />
          </View>
        )
      })}
    </View>
  )
}

export default Row
